import React from "react";
import { createPortal } from "react-dom";
import { CardDefinition } from "../api/respectCardsApi";
import { API_BASE_URL } from "../../../config/env";
import { respectCardsPlayerPaths } from "../api/apiPaths";
import { formatCardDuration } from "../lib/utils";

export type RespectCardSize = "xs" | "sm" | "md" | "lg";

const RARITY_LABEL: Record<string, string> = {
  common: "Common",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
};

function rarityWord(rarity: string): string {
  return RARITY_LABEL[rarity] ?? rarity;
}

export interface RespectCardProps {
  card: CardDefinition | null;
  size?: RespectCardSize;
  className?: string;
  /** when false, show an empty placeholder slot (e.g. pool cards before your pull) */
  showDetails?: boolean;
  /** for size xs: tap/click opens full-size overlay; set false for static mini previews (e.g. admin) */
  expandable?: boolean;
}

/** GCS objects are often private; <img> cannot send auth, so use public BFF /api/cards/art/:slug instead of storage.googleapis.com. */
function resolvedCardArtSrc(card: CardDefinition, rawUrl: string): string {
  if (rawUrl.startsWith("data:")) {
    return rawUrl;
  }
  const slug = String(card.slug || card.id || "").trim();
  if (!slug || !(rawUrl.startsWith("http://") || rawUrl.startsWith("https://"))) {
    return rawUrl;
  }
  try {
    const u = new URL(rawUrl);
    if (u.hostname === "storage.googleapis.com") {
      return `${API_BASE_URL}${respectCardsPlayerPaths.cardArt(slug)}`;
    }
  } catch {
    /* use raw */
  }
  return rawUrl;
}

const CATEGORY_LABELS: Record<string, string> = {
  aotd: "Art of the Day",
  comments: "Comments",
  economy: "Economy",
  social: "Social",
  notification: "Notifications",
  "agent-influence": "Influence game",
};

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat.replace(/_/g, " ");
}

function frameClass(rarity: string, size: RespectCardSize, className?: string): string {
  return ["respect-tcg", `respect-tcg--${rarity}`, `respect-tcg--${size || "md"}`, className || ""]
    .filter(Boolean)
    .join(" ");
}

function RespectCardFace({
  card,
  size,
  className,
  suppressAriaLabel,
}: {
  card: CardDefinition;
  size: RespectCardSize;
  className?: string;
  /** parent (trigger or dialog) already names this region */
  suppressAriaLabel?: boolean;
}) {
  const rarity = card.rarity || "common";
  const category = typeof card.category === "string" ? card.category : "aotd";
  const emoji = card.emoji || "🃏";
  // Footer reads the honest effect lifetime when the server sent one; falls
  // back to the coarse effect-duration for legacy rows without it.
  const footLabel =
    card.effectLifetime?.label ??
    formatCardDuration(card.durationHours ?? null) ??
    "Instant";
  const artUrl = card.imageUrl && card.imageUrl.length > 0 ? card.imageUrl : null;
  const displaySrc = artUrl ? resolvedCardArtSrc(card, artUrl) : null;
  // A failing art proxy (deleted GCS object, non-bucket URL) should fall back
  // to the emoji tile, not leave a blank art box.
  const [artFailed, setArtFailed] = React.useState(false);
  React.useEffect(() => {
    setArtFailed(false);
  }, [displaySrc]);
  const showArt = Boolean(displaySrc) && !artFailed;

  return (
    <article
      className={frameClass(rarity, size, className)}
      {...(!suppressAriaLabel ? { "aria-label": `${card.name} card` } : {})}
    >
      <div className="respect-tcg__frame">
        <header className="respect-tcg__titlebar">
          <span className="respect-tcg__title-emoji" aria-hidden>
            {emoji}
          </span>
          <span className="respect-tcg__name">{card.name}</span>
          <span className="respect-tcg__rarity-dot" aria-hidden />
        </header>

        <div className="respect-tcg__art">
          {showArt && displaySrc ? (
            <img
              className="respect-tcg__art-img"
              src={displaySrc}
              alt=""
              loading="lazy"
              onError={() => setArtFailed(true)}
            />
          ) : (
            <div className="respect-tcg__art-fallback" aria-hidden>
              <span className="respect-tcg__art-emoji">{emoji}</span>
            </div>
          )}
        </div>

        <div className="respect-tcg__typebar">
          {categoryLabel(category)}
          <span className="respect-tcg__typebar-sep" aria-hidden>
            {" · "}
          </span>
          <span className={`respect-tcg__typebar-rarity respect-tcg__typebar-rarity--${rarity}`}>
            {rarityWord(rarity)}
          </span>
        </div>

        <div className="respect-tcg__textbox">
          <p className="respect-tcg__desc">{card.description}</p>
          {card.flavor ? <p className="respect-tcg__flavor">{card.flavor}</p> : null}
        </div>

        <footer className="respect-tcg__foot">
          <span className="respect-tcg__foot-rarity">{rarityWord(rarity)}</span>
          <span className="respect-tcg__foot-dur">{footLabel}</span>
        </footer>
      </div>
    </article>
  );
}

export default function RespectCard({
  card,
  size = "md",
  className,
  showDetails = true,
  expandable,
}: RespectCardProps) {
  const rarity = card?.rarity || "common";
  const [expanded, setExpanded] = React.useState(false);

  const isXsExpandable =
    size === "xs" && showDetails && card && expandable !== false;

  React.useEffect(() => {
    if (!expanded) {
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  // A null definition (retired / unresolvable card) is NOT a face-down slot —
  // it renders a labeled grey "Unknown card" ghost so the surface reads
  // honestly instead of showing a mystery daily-drop back.
  if (!card) {
    return (
      <div
        className={`${frameClass("common", size, className)} respect-tcg--unknown`}
        aria-label="Unknown card"
      >
        <div className="respect-tcg__unknown-inner">
          <span className="respect-tcg__unknown-mark" aria-hidden>
            🂠
          </span>
          <span className="respect-tcg__unknown-hint">Unknown card</span>
        </div>
      </div>
    );
  }

  if (!showDetails) {
    return (
      <div
        className={`${frameClass(rarity, size, className)} respect-tcg--back`}
        aria-label="face-down card slot"
      >
        <div className="respect-tcg__back-inner">
          <span className="respect-tcg__back-mark">n450s</span>
          <span className="respect-tcg__back-hint">Daily drop</span>
        </div>
      </div>
    );
  }

  if (isXsExpandable) {
    return (
      <>
        <div
          className={["respect-tcg-expand__trigger", className || ""].filter(Boolean).join(" ")}
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded(true);
            }
          }}
          aria-expanded={expanded}
          aria-haspopup="dialog"
          aria-label={`${card.name}, view full card`}
        >
          <RespectCardFace card={card} size="xs" suppressAriaLabel />
        </div>
        {expanded
          ? createPortal(
              <div
                className="respect-tcg-expand"
                role="dialog"
                aria-modal="true"
                aria-label={`${card.name} — full card`}
              >
                <button
                  type="button"
                  className="respect-tcg-expand__backdrop"
                  aria-label="Close"
                  onClick={() => setExpanded(false)}
                />
                <div className="respect-tcg-expand__panel">
                  <RespectCardFace card={card} size="md" suppressAriaLabel />
                  <button
                    type="button"
                    className="respect-tcg-expand__close"
                    onClick={() => setExpanded(false)}
                  >
                    Close
                  </button>
                </div>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  }

  return <RespectCardFace card={card} size={size} className={className} />;
}
